import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowLeft, Clock, Shield, User } from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import RiskGauge from '../components/RiskGauge';
import SeverityBadge from '../components/SeverityBadge';
import StatusBadge from '../components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import api, { alertsApi, analyticsApi, employeesApi } from '../lib/api';

function formatTimestamp(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatDuration(seconds) {
  const totalMinutes = Math.round((seconds || 0) / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
}

function durationBetween(start, end) {
  return Math.max(0, Math.round((new Date(end) - new Date(start)) / 1000));
}

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [riskHistory, setRiskHistory] = useState([]);
  const [activityRows, setActivityRows] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [productivity, setProductivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadEmployee = async () => {
      setLoading(true);
      setError('');
      const today = new Date().toISOString().slice(0, 10);
      const monthAgo = new Date(Date.now() - 29 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10);
      try {
        const [employeeResponse, riskResponse, activityResponse, alertsResponse, productivityResponse] =
          await Promise.all([
            employeesApi.get(id),
            analyticsApi.employeeRisk(id).catch(() => ({ data: { items: [] } })),
            api.get('/export/activity', {
              params: { date_from: monthAgo, date_to: today, page: 1, page_size: 500 },
            }),
            api.get('/export/alerts', {
              params: { date_from: monthAgo, date_to: today, page: 1, page_size: 500 },
            }),
            analyticsApi.productivity({
              employee_id: id,
              start_date: today,
              end_date: today,
            }).catch(() => ({ data: null })),
          ]);

        setEmployee(employeeResponse.data);
        setRiskHistory(riskResponse.data?.items || []);
        setActivityRows(
          (activityResponse.data?.items || []).filter((row) => String(row.employee_id) === String(id))
        );
        setAlerts(
          (alertsResponse.data?.items || []).filter((row) => String(row.employee_id) === String(id))
        );
        setProductivity(productivityResponse.data);
      } catch (err) {
        const message = err.response?.data?.detail || 'Failed to load employee detail.';
        setError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    };

    loadEmployee();
  }, [id]);

  const latestRisk = riskHistory[0];
  const activeHoursToday = formatDuration(productivity?.active_sec || 0);
  const overviewStats = [
    { label: 'Total Events', value: activityRows.length },
    { label: 'Active Hours Today', value: activeHoursToday },
    { label: 'Risk Score', value: latestRisk ? Math.round(latestRisk.overall_risk) : '—' },
    { label: 'Alerts', value: alerts.length },
  ];

  const chartData = useMemo(
    () =>
      [...riskHistory]
        .reverse()
        .map((item) => ({
          ts: new Intl.DateTimeFormat(undefined, {
            month: 'short',
            day: 'numeric',
          }).format(new Date(item.calculated_at)),
          score: item.overall_risk,
        })),
    [riskHistory]
  );

  if (loading) {
    return (
      <div className="min-h-screen space-y-6 bg-slate-900 p-6 text-slate-300">
        <LoadingSkeleton type="card" />
        <LoadingSkeleton type="chart" />
        <LoadingSkeleton type="table" />
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="min-h-screen bg-slate-900 p-6 text-slate-300">
        <EmptyState
          icon={<User className="h-8 w-8" />}
          title="Employee unavailable"
          description={error || 'The selected employee could not be loaded.'}
          action={
            <button
              className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
              onClick={() => navigate('/employees')}
            >
              Back to Employees
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen space-y-6 bg-slate-900 p-6 text-slate-300">
      <div className="flex items-start gap-3">
        <button
          className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-slate-300 hover:bg-slate-700"
          onClick={() => navigate('/employees')}
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">{employee.name}</h1>
          <p className="mt-2 text-slate-500">
            {employee.email} • {employee.custom_id || employee.id} • {employee.team_name || 'Unassigned department'} • Joined{' '}
            {formatTimestamp(employee.created_at)}
          </p>
        </div>
      </div>

      <Card className="border-slate-700 bg-slate-800 shadow-lg">
        <CardContent className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-[1.2fr,220px]">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-xl font-semibold text-white">Profile</h2>
              <StatusBadge status={activityRows.length ? 'online' : 'offline'} />
              <SeverityBadge severity={latestRisk?.risk_level || 'info'} />
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-slate-700 bg-slate-900/70 p-4">
                <p className="text-sm text-slate-500">Department</p>
                <p className="mt-2 text-white">{employee.team_name || 'Unassigned'}</p>
              </div>
              <div className="rounded-lg border border-slate-700 bg-slate-900/70 p-4">
                <p className="text-sm text-slate-500">Status</p>
                <p className="mt-2 text-white">{activityRows.length ? 'Active' : 'Inactive'}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center">
            <RiskGauge score={latestRisk?.overall_risk || 0} size="lg" />
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="bg-slate-800 text-slate-400">
          <TabsTrigger className="data-[state=active]:bg-slate-900 data-[state=active]:text-white" value="overview">
            Overview
          </TabsTrigger>
          <TabsTrigger className="data-[state=active]:bg-slate-900 data-[state=active]:text-white" value="activity">
            Activity
          </TabsTrigger>
          <TabsTrigger className="data-[state=active]:bg-slate-900 data-[state=active]:text-white" value="alerts">
            Alerts
          </TabsTrigger>
          <TabsTrigger className="data-[state=active]:bg-slate-900 data-[state=active]:text-white" value="risk">
            Risk History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {overviewStats.map((item) => (
              <Card key={item.label} className="border-slate-700 bg-slate-800 shadow-lg">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">{item.label}</p>
                  <p className="mt-2 text-3xl font-bold text-white">{item.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="activity">
          <Card className="border-slate-700 bg-slate-800 shadow-lg">
            <CardContent className="p-6">
              <DataTable
                columns={[
                  { key: 'app_name', label: 'App' },
                  {
                    key: 'window_title',
                    label: 'Window',
                    render: (value) => value || '—',
                  },
                  {
                    key: 'duration',
                    label: 'Duration',
                    render: (_, row) => formatDuration(durationBetween(row.ts_start, row.ts_end)),
                  },
                  {
                    key: 'ts_start',
                    label: 'Start Time',
                    render: (value) => formatTimestamp(value),
                  },
                ]}
                data={activityRows.slice(0, 20)}
                loading={false}
                emptyMessage="No activity events found for this employee."
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts">
          <Card className="border-slate-700 bg-slate-800 shadow-lg">
            <CardContent className="p-6">
              <DataTable
                columns={[
                  {
                    key: 'severity',
                    label: 'Severity',
                    render: (value) => <SeverityBadge severity={value} />,
                  },
                  {
                    key: 'type',
                    label: 'Type',
                    render: (value) => String(value || '').replace(/_/g, ' '),
                  },
                  {
                    key: 'status',
                    label: 'Status',
                    render: (value) => <StatusBadge status={value || 'open'} />,
                  },
                  {
                    key: 'ts',
                    label: 'Timestamp',
                    render: (value) => formatTimestamp(value),
                  },
                ]}
                data={alerts.slice(0, 20)}
                loading={false}
                emptyMessage="No alerts are associated with this employee."
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="risk">
          <Card className="border-slate-700 bg-slate-800 shadow-lg">
            <CardHeader className="pb-0">
              <CardTitle className="text-white">Risk Trend</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {chartData.length ? (
                <div className="h-[320px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                      <XAxis dataKey="ts" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                      <YAxis domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1e293b',
                          border: '1px solid #334155',
                          borderRadius: '12px',
                          color: '#e2e8f0',
                        }}
                      />
                      <Line dataKey="score" stroke="#8b5cf6" strokeWidth={2.5} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <EmptyState
                  icon={<Shield className="h-8 w-8" />}
                  title="No risk history"
                  description="Risk history will appear after the scoring worker has produced snapshots."
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
